import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type { PermissionGroupsResponse } from 'src/apis/authorization-service/types';
import type { AuthorizationServiceRequest } from 'src/apis/authorization-service/use-authorization-service';
import type { Successful } from 'src/hooks/use-axios';
import { useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';

type HookProps = {
  authorizationService?: AuthorizationServiceRequest;
};

type ExecuteProps = {
  authorizationService: Successful<AuthorizationServiceRequest>;
};

export function usePermissionGroups({ authorizationService }: HookProps) {
  const request = useAxios(
    (axios, baseConfig, { authorizationService }: ExecuteProps) => {
      if (!authorizationService.response.data._links.permissionGroups)
        throw new Forbidden();

      const url = urlTemplate
        .parse(authorizationService.response.data._links.permissionGroups.href)
        .expand({});

      return axios.request<PermissionGroupsResponse>({
        ...baseConfig,
        url,
      });
    },
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    if (!authorizationService?.response) return;
    request.execute({ authorizationService });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizationService?.response]);

  if (!authorizationService) return;

  return request;
}

export type PermissionGroupsRequest = ReturnType<typeof usePermissionGroups>;
